import { FC } from 'react';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  Link as ChakraLink,
  Stack,
  Text,
  VStack
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { DeploymentData } from 'pages/Onboarding';
import { Link } from 'react-router-dom';
import { EtherscanLinkType, getEtherscanLink } from 'utils/getEtherscanLink';
import { convertChainIdToNetworkName } from 'utils/convertChainIdToNetworkName';
import { useWeb3React } from 'hooks/web3';

type Props = {
  deploymentData: DeploymentData;
};

const Summary: FC<Props> = ({ deploymentData }) => {
  const { chainId = 1 } = useWeb3React();
  return (
    <>
      <Heading size='md' mb={8}>
        Program Metadata
      </Heading>
      <Grid
        p={10}
        mb={10}
        borderRadius='xl'
        bg='gray.700'
        templateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(3, 1fr)']}
        gap={6}
      >
        <GridItem>
          <Stack direction={['row', 'column']}>
            <Heading size='sm'>Name</Heading>
            <Text>{deploymentData.name}</Text>
          </Stack>
        </GridItem>
        <GridItem>
          <Stack direction={['row', 'column']}>
            <Heading size='sm'>Start</Heading>
            <Text>
              {deploymentData.startTime &&
                dayjs(dayjs.unix(deploymentData.startTime / 1000)).format(
                  'DD/MM/YYYY - HH:mm'
                )}
            </Text>
          </Stack>
        </GridItem>
        <GridItem colSpan={[2, null, 1]}>
          <Stack direction={['row', 'column']}>
            <Heading size='sm'>Reward program address</Heading>
            <ChakraLink
              href={getEtherscanLink(
                chainId,
                deploymentData.programAddress,
                EtherscanLinkType.ADDRESS
              )}
              isExternal
              color='teal.200'
            >
              {deploymentData.programAddress}
            </ChakraLink>
          </Stack>
        </GridItem>
      </Grid>

      <Heading size='md' mb={8}>
        Additional Information
      </Heading>
      <Stack
        p={10}
        mb={10}
        borderRadius='xl'
        bg='gray.700'
        direction={['column', null, 'row']}
      >
        <VStack alignItems={['flex-start']} spacing={25} pr={10}>
          <VStack alignItems={['flex-start']}>
            <Heading size='sm'>Network/Chain</Heading>
            <Text>{convertChainIdToNetworkName(chainId)}</Text>
          </VStack>
          <VStack alignItems={['flex-start']}>
            <Heading size='sm'>Staking token address</Heading>
            <Text>
              <ChakraLink
                href={getEtherscanLink(
                  chainId,
                  deploymentData.stakingToken.address,
                  EtherscanLinkType.ADDRESS
                )}
                isExternal
                color='teal.200'
              >
                {deploymentData.stakingToken.address}
              </ChakraLink>
            </Text>
          </VStack>
          <VStack alignItems={['flex-start']}>
            <Heading size='sm'>Reward token address</Heading>
            <ChakraLink
              href={getEtherscanLink(
                chainId,
                deploymentData.rewardToken.address,
                EtherscanLinkType.ADDRESS
              )}
              isExternal
              color='teal.200'
            >
              {deploymentData.rewardToken.address}
            </ChakraLink>
          </VStack>
          {deploymentData.bonusTokens.length > 0 ? (
            <VStack alignItems={['flex-start']}>
              <Heading size='sm'>Bonus token addresses</Heading>
              {deploymentData.bonusTokens.map((token, idx) => (
                <ChakraLink
                  key={idx}
                  href={getEtherscanLink(
                    chainId,
                    token.address,
                    EtherscanLinkType.ADDRESS
                  )}
                  isExternal
                  color='teal.200'
                >
                  {token.address}
                </ChakraLink>
              ))}
            </VStack>
          ) : (
            <></>
          )}
          <VStack alignItems={['flex-start']}>
            <Heading size='sm'>Owner addresses</Heading>
            <ChakraLink
              href={getEtherscanLink(
                chainId,
                deploymentData.ownerAddress,
                EtherscanLinkType.ADDRESS
              )}
              isExternal
              color='teal.200'
            >
              {deploymentData.ownerAddress}
            </ChakraLink>
          </VStack>
        </VStack>
        <VStack alignItems={['flex-start']} pt={[5, null, 0]}>
          <Heading size='sm'>Multiplier Configuration</Heading>
          {deploymentData.scalingFloor === deploymentData.scalingCeiling ? (
            <Text>Disabled</Text>
          ) : (
            <>
              <Text>Scaling floor: {deploymentData.scalingFloor}</Text>
              <Text>Scaling ceiling: {deploymentData.scalingCeiling}</Text>
              <Text>Scaling days: {deploymentData.scalingDays}</Text>
            </>
          )}
        </VStack>
      </Stack>

      <Box textAlign='end'>
        <Link to={`/manage/${deploymentData.programAddress}`} color='black'>
          <Button>Manage</Button>
        </Link>
      </Box>
    </>
  );
};

export default Summary;
